import React, { useState, useEffect } from 'react';
import { Modal, Button, Card, Typography, Alert, Progress } from 'antd';
import { AudioOutlined, StopOutlined, SaveOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useSupabaseAuth } from '@/utils/store/supabaseAuth';
import { useRealtimeTranscription } from '@/hooks/useRealtimeTranscription';

const { Title, Text } = Typography;

interface OnlineMeetModalProps {
  visible: boolean;
  onClose: () => void;
  onMeetingEnd: (notes: string, transcription: string) => void;
}

/**
 * 在线会议弹窗组件
 * 包含左侧笔记区域(3/4)和右侧实时转录区域(1/4)
 */
const OnlineMeetModal: React.FC<OnlineMeetModalProps> = ({ visible, onClose, onMeetingEnd }) => {
  const { isAuthenticated } = useSupabaseAuth();
  const [notes, setNotes] = useState('');

  // 使用实时转录 hook
  const {
    isRecording,
    isConnected,
    finalText,
    partialText,
    error,
    microphonePermission,
    permissionError,
    durationState,
    startRecording,
    stopRecording,
    clearTranscription,
    requestMicrophonePermission,
  } = useRealtimeTranscription({
    moduleName: 'OnlineMeet',
    onStart: () => {
      console.log('OnlineMeet: 录音已开始');
    },
    onStop: (transcription) => {
      console.log('OnlineMeet: 录音已停止，转录文本长度:', transcription.length);
    },
    onError: (err) => {
      console.error('OnlineMeet: 转录错误:', err);
    },
  });

  /**
   * 清空笔记内容
   */
  const clearNotes = () => {
    setNotes('');
  };

  /**
   * 结束会议并保存内容
   */
  const handleMeetingEnd = () => {
    if (isRecording) {
      stopRecording();
    }
    onMeetingEnd(notes, finalText);
  };

  /**
   * 关闭弹窗时的清理工作
   */
  const handleClose = () => {
    if (isRecording) {
      stopRecording();
    }
    onClose();
  };

  // 弹窗打开时申请麦克风权限
  useEffect(() => {
    if (visible && microphonePermission === 'pending') {
      requestMicrophonePermission();
    }
  }, [visible, microphonePermission, requestMicrophonePermission]);

  return (
    <Modal
      title="在线会议"
      open={visible}
      onCancel={handleClose}
      width="90vw"
      style={{ top: 20 }}
      styles={{ 
        body: { height: '80vh', padding: 0 },
        mask: { backdropFilter: 'blur(8px)' }
      }}
      footer={[
        <Button key="clear-notes" onClick={clearNotes}>
          清空笔记
        </Button>,
        <Button key="clear-transcription" onClick={clearTranscription}>
          清空转录
        </Button>,
        <Button key="cancel" onClick={handleClose}>
          取消
        </Button>,
        <Button key="save" type="primary" onClick={handleMeetingEnd} icon={<SaveOutlined />}>
          结束会议并保存
        </Button>,
      ]}
    >
      <div className="flex h-full">
        {/* 左侧笔记区域 - 占3/4 */}
        <div className="flex-1 w-3/4 p-4 border-r border-gray-200">
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <Title level={4} className="m-0">📝 会议笔记</Title>
            </div>
            <textarea
              className="flex-1 w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="在此记录会议要点、决策事项、行动计划等..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{ minHeight: '500px' }}
            />
          </div>
        </div>

        {/* 右侧实时转录区域 - 占1/4 */}
        <div className="w-1/4 p-4 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <Title level={4} className="m-0">🎤 实时转录</Title>
          </div>

          {/* 录音控制按钮 */}
          <div className="mb-4 space-y-2">
            {/* 时长显示 */}
            <Card size="small" className="mb-3">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    <ClockCircleOutlined className="mr-1" />
                    录音时长
                  </span>
                  <span className="text-sm font-semibold">
                    {durationState.getFormattedDuration()}
                  </span>
                </div>
                <Progress 
                  percent={durationState.getUsagePercentage()} 
                  size="small"
                  status={durationState.isLimitReached ? 'exception' : 'active'}
                  showInfo={false}
                />
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>剩余: {Math.floor(durationState.getRemainingDuration() / 60)} 分钟</span>
                  <span>上限: {Math.floor(durationState.maxDuration / 60)} 分钟</span>
                </div>
              </div>
            </Card>

            {microphonePermission === 'denied' && (
              <Alert
                message="麦克风权限被拒绝或未检测到麦克风"
                description={permissionError}
                type="error"
                showIcon
                action={
                  <Button size="small" onClick={requestMicrophonePermission}>
                    重新申请
                  </Button>
                }
              />
            )}

            {microphonePermission === 'granted' && (
              <div className="flex flex-col space-y-2">
                {!isRecording ? (
                  <Button
                    type="primary"
                    icon={<AudioOutlined />}
                    onClick={startRecording}
                    disabled={!isAuthenticated}
                    block
                  >
                    开始录音
                  </Button>
                ) : (
                  <Button
                    danger
                    icon={<StopOutlined />}
                    onClick={stopRecording}
                    block
                  >
                    停止录音
                  </Button>
                )}

                <div className="text-center">
                  {isRecording && (
                    <div className="flex items-center justify-center space-x-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                      <Text type="secondary">录音中...</Text>
                    </div>
                  )}
                  {isConnected && !isRecording && (
                    <Text type="success">已连接</Text>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* 转录内容显示区域 */}
          <div className="flex-1 border border-gray-300 rounded-lg p-3 overflow-y-auto bg-gray-50">
            <div className="space-y-2">
              {finalText && (
                <div className="text-sm text-gray-800 leading-relaxed">
                  {finalText}
                </div>
              )}
              {partialText && (
                <div className="text-sm text-gray-500 italic">
                  {partialText}
                </div>
              )}
              {!finalText && !partialText && (
                <div className="text-center text-gray-400 text-sm">
                  转录内容将在此显示
                </div>
              )}
            </div>
          </div>

          {error && (
            <Alert
              message="错误"
              description={error}
              type="error"
              showIcon
              className="mt-2"
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default OnlineMeetModal;